import { Component, ElementRef, EventEmitter, Input, OnInit, OnDestroy, Output, ChangeDetectorRef, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RequestsV2ImplService } from '../../../../services/requests-v2.impl.service';
import { MessageService } from '../../../../../global/services/message/message.abstract.service';
import { CreateRequestStatusDto, RequestStatus, StatusFormModel, UpdateRequestStatusDto } from '../../../../models/request-status.model';
import { RequestStatusService } from '../../../../services/request-status.service';

interface SelectOption {
    value: string;
    label: string;
}

@Component({
    selector: 'app-status-form',
    templateUrl: './status-form.component.html',
    styleUrls: ['./status-form.component.scss'],
})
export class StatusFormComponent implements OnInit, OnDestroy, OnChanges {
    @Input() statusIdToEdit: string | null = null;

    @Output() cancel = new EventEmitter<void>();

    @Output() saved = new EventEmitter<RequestStatus>();

    statusForm: FormGroup;

    isSubmitting = false;

    isLoading = false;

    statusTypeOptions: SelectOption[] = [];

    defaultStatusTypes: RequestStatus[] = [];

    statusToEdit: StatusFormModel | null = null;

    statusTypeOptionsLoaded = false;

    statusToEditLoaded = false;

    private destroy$ = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private elementRef: ElementRef,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
        private messageService: MessageService,
        private requestsService: RequestsV2ImplService,
        private requestStatusService: RequestStatusService,
    ) {}

    ngOnInit(): void {
        this.initForm();
        this.loadStatusTypes();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.statusIdToEdit && changes.statusIdToEdit.currentValue && this.statusIdToEdit) {
            this.loadStatusDetails(this.statusIdToEdit);
        }
    }

    private loadStatusDetails(statusId: string): void {
        this.isLoading = true;
        this.statusToEditLoaded = false;
        this.requestStatusService
            .getStatusEdition(statusId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (statusData) => {
                    this.statusToEdit = statusData;
                    this.statusToEditLoaded = true;
                    if (this.statusForm) {
                        this.statusForm.patchValue({
                            name: {
                                en: this.statusToEdit.name.en || '',
                                fr: this.statusToEdit.name.fr || '',
                            },
                        });
                    }
                    this.setStatusTypeIfReady();
                    this.isLoading = false;
                    this.cdr.detectChanges();
                },
                error: (_err) => {
                    this.isLoading = false;
                    this.showErrorMessage('request_v2.status_form.load_error');
                },
            });
    }

    private loadStatusTypes(): void {
        this.statusTypeOptionsLoaded = false;
        this.requestStatusService
            .getDefaultStatusList()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (statusTypes) => {
                    if (!statusTypes || statusTypes.length === 0) {
                        return;
                    }
                    this.defaultStatusTypes = statusTypes;
                    const options = [];
                    statusTypes.forEach((status) => {
                        try {
                            const labelText = status.name;
                            options.push({
                                value: status._id,
                                label: labelText,
                            });
                        } catch (err) {}
                    });
                    this.statusTypeOptions = options;
                    this.statusTypeOptionsLoaded = true;
                    this.setStatusTypeIfReady();
                    this.cdr.detectChanges();
                },
                error: (_err) => {
                    this.showErrorMessage('request_v2.status_form.status_type_load_error');
                    this.statusTypeOptions = [];
                },
            });
    }

    private setStatusTypeIfReady(): void {
        if (this.statusTypeOptionsLoaded && this.statusToEditLoaded && this.statusToEdit && this.statusForm) {
            this.statusForm.get('statusType').setValue(this.statusToEdit.type || '');
        }
    }

    private initForm(): void {
        this.statusForm = this.fb.group({
            name: this.fb.group({
                en: ['', Validators.required],
                fr: ['', Validators.required],
            }),
            statusType: ['', Validators.required],
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onCancel(): void {
        this.cancel.emit();
    }

    onSubmit(): void {
        if (!this.statusForm) {
            return;
        }

        if (this.statusForm.invalid) {
            this.markFormGroupTouched(this.statusForm);
            return;
        }

        if (this.isSubmitting) {
            return;
        }

        this.isSubmitting = true;
        const formValue = this.statusForm.value;

        let statusTypeId = formValue.statusType;

        if (statusTypeId && typeof statusTypeId === 'object' && statusTypeId.value) {
            statusTypeId = statusTypeId.value;
        }

        if (!statusTypeId) {
            this.isSubmitting = false;
            return;
        }

        if (this.statusIdToEdit && this.statusToEdit) {
            const updateStatusDto: UpdateRequestStatusDto = {
                _id: this.statusIdToEdit,
                name: formValue.name,
                type: statusTypeId,
            };

            this.requestStatusService
                .update(this.statusIdToEdit, updateStatusDto)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (savedStatus) => {
                        this.isSubmitting = false;
                        this.showSuccessMessage('request_v2.status_form.update_success');
                        this.saved.emit(savedStatus);
                    },
                    error: (_err) => {
                        this.isSubmitting = false;
                        this.showErrorMessage('request_v2.status_form.update_error');
                    },
                });
        } else {
            const createStatusDto: CreateRequestStatusDto = {
                name: formValue.name,
                type: statusTypeId,
            };

            this.requestStatusService
                .create(createStatusDto)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (savedStatus) => {
                        this.isSubmitting = false;
                        this.showSuccessMessage('request_v2.status_form.create_success');
                        this.saved.emit(savedStatus);
                    },
                    error: (_err) => {
                        this.isSubmitting = false;
                        this.showErrorMessage('request_v2.status_form.create_error');
                    },
                });
        }
    }

    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach((control) => {
            control.markAsTouched();

            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            }
        });
    }

    private showSuccessMessage(key: string): void {
        this.messageService.success(this.translate.instant(key));
    }

    private showErrorMessage(key: string): void {
        this.messageService.error(this.translate.instant(key));
    }
}
